// Permission checking utilities

/**
 * Check if user has a specific permission
 * @param {Array} userPermissions - Array of user's permissions
 * @param {string} requiredPermission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  return userPermissions.includes(requiredPermission);
};

/**
 * Check if user has any of the specified permissions
 * @param {Array} userPermissions - Array of user's permissions
 * @param {Array} requiredPermissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  if (!requiredPermissions || !Array.isArray(requiredPermissions)) return false;
  return requiredPermissions.some(permission => userPermissions.includes(permission));
};

/**
 * Check if user has all of the specified permissions
 * @param {Array} userPermissions - Array of user's permissions
 * @param {Array} requiredPermissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAllPermissions = (userPermissions, requiredPermissions) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  if (!requiredPermissions || !Array.isArray(requiredPermissions)) return false;
  return requiredPermissions.every(permission => userPermissions.includes(permission));
};

/**
 * Check if user can view a specific section
 * @param {Array} userPermissions - Array of user's permissions
 * @param {string} section - Section name (e.g., 'Users', 'Forms', 'Tasks')
 * @returns {boolean}
 */
export const canView = (userPermissions, section) => {
  return hasPermission(userPermissions, `View ${section}`);
};

/**
 * Check if user can create in a specific section
 * @param {Array} userPermissions - Array of user's permissions
 * @param {string} section - Section name (e.g., 'Users', 'Forms', 'Tasks')
 * @returns {boolean}
 */
export const canCreate = (userPermissions, section) => {
  // Convert section to singular form for create/update/delete actions
  const singularSection = getSingularForm(section);
  return hasPermission(userPermissions, `Create ${singularSection}`);
};

/**
 * Check if user can update in a specific section
 * @param {Array} userPermissions - Array of user's permissions
 * @param {string} section - Section name (e.g., 'Users', 'Forms', 'Tasks')
 * @returns {boolean}
 */
export const canUpdate = (userPermissions, section) => {
  // Convert section to singular form for create/update/delete actions
  const singularSection = getSingularForm(section);
  return hasPermission(userPermissions, `Update ${singularSection}`);
};

/**
 * Check if user can delete in a specific section
 * @param {Array} userPermissions - Array of user's permissions
 * @param {string} section - Section name (e.g., 'Users', 'Forms', 'Tasks')
 * @returns {boolean}
 */
export const canDelete = (userPermissions, section) => {
  // Convert section to singular form for create/update/delete actions
  const singularSection = getSingularForm(section);
  return hasPermission(userPermissions, `Delete ${singularSection}`);
};

// User-specific permission helpers
export const canResetPassword = (userPermissions) => {
  return hasPermission(userPermissions, 'Reset User Password');
};

export const canChangeUserStatus = (userPermissions) => {
  return hasPermission(userPermissions, 'Change User Status');
};

export const canAssignUserToGroup = (userPermissions) => {
  return hasPermission(userPermissions, 'Assign User to Group');
};

export const canExportUsers = (userPermissions) => {
  return hasPermission(userPermissions, 'Export Users');
};

export const canImportUsers = (userPermissions) => {
  return hasPermission(userPermissions, 'Import Users');
};

// Group-specific permission helpers
export const canCloneGroup = (userPermissions) => {
  return hasPermission(userPermissions, 'Clone Group');
};

export const canAssignUsersToGroup = (userPermissions) => {
  return hasPermission(userPermissions, 'Assign Users to Group');
};

export const canRemoveUsersFromGroup = (userPermissions) => {
  return hasPermission(userPermissions, 'Remove Users from Group');
};

export const canViewGroupMembers = (userPermissions) => {
  return hasPermission(userPermissions, 'View Group Members');
};

export const canExportGroups = (userPermissions) => {
  return hasPermission(userPermissions, 'Export Groups');
};

export const canImportGroups = (userPermissions) => {
  return hasPermission(userPermissions, 'Import Groups');
};

// Task-specific permission helpers
export const canAssignTask = (userPermissions) => {
  return hasPermission(userPermissions, 'Assign Task');
};

export const canChangeTaskStatus = (userPermissions) => {
  return hasPermission(userPermissions, 'Change Task Status');
};

export const canAddTaskComment = (userPermissions) => {
  return hasPermission(userPermissions, 'Add Task Comment');
};

export const canUpdateTaskComment = (userPermissions) => {
  return hasPermission(userPermissions, 'Update Task Comment');
};

export const canDeleteTaskComment = (userPermissions) => {
  return hasPermission(userPermissions, 'Delete Task Comment');
};

export const canUploadTaskAttachment = (userPermissions) => {
  return hasPermission(userPermissions, 'Upload Task Attachment');
};

export const canDownloadTaskAttachment = (userPermissions) => {
  return hasPermission(userPermissions, 'Download Task Attachment');
};

export const canDeleteTaskAttachment = (userPermissions) => {
  return hasPermission(userPermissions, 'Delete Task Attachment');
};

export const canViewTaskHistory = (userPermissions) => {
  return hasPermission(userPermissions, 'View Task History');
};

export const canSetTaskPriority = (userPermissions) => {
  return hasPermission(userPermissions, 'Set Task Priority');
};

export const canSetTaskDueDate = (userPermissions) => {
  return hasPermission(userPermissions, 'Set Task Due Date');
};

export const canMarkTaskComplete = (userPermissions) => {
  return hasPermission(userPermissions, 'Mark Task as Complete');
};

export const canReopenTask = (userPermissions) => {
  return hasPermission(userPermissions, 'Reopen Task');
};

export const canExportTasks = (userPermissions) => {
  return hasPermission(userPermissions, 'Export Tasks');
};

export const canImportTasks = (userPermissions) => {
  return hasPermission(userPermissions, 'Import Tasks');
};

// Form-specific permission helpers
export const canDuplicateForm = (userPermissions) => {
  return hasPermission(userPermissions, 'Duplicate Form');
};

export const canPublishForm = (userPermissions) => {
  return hasPermission(userPermissions, 'Publish Form');
};

export const canUnpublishForm = (userPermissions) => {
  return hasPermission(userPermissions, 'Unpublish Form');
};

export const canViewFormAnalytics = (userPermissions) => {
  return hasPermission(userPermissions, 'View Form Analytics');
};

export const canExportFormData = (userPermissions) => {
  return hasPermission(userPermissions, 'Export Form Data');
};

// Form Submission-specific permission helpers
export const canApproveSubmission = (userPermissions) => {
  return hasPermission(userPermissions, 'Approve Form Submission');
};

export const canRejectSubmission = (userPermissions) => {
  return hasPermission(userPermissions, 'Reject Form Submission');
};

export const canAddSubmissionComment = (userPermissions) => {
  return hasPermission(userPermissions, 'Add Submission Comment');
};

export const canAssignSubmission = (userPermissions) => {
  return hasPermission(userPermissions, 'Assign Submission to User');
};

export const canViewSubmissionHistory = (userPermissions) => {
  return hasPermission(userPermissions, 'View Submission History');
};

// File Upload permission helpers
export const canUploadFiles = (userPermissions) => {
  return hasPermission(userPermissions, 'Upload Files');
};

export const canDownloadFiles = (userPermissions) => {
  return hasPermission(userPermissions, 'Download Files');
};

export const canDeleteFiles = (userPermissions) => {
  return hasPermission(userPermissions, 'Delete Files');
};

export const canViewUploadHistory = (userPermissions) => {
  return hasPermission(userPermissions, 'View Upload History');
};

// Checklist permission helpers
export const canApproveDocument = (userPermissions) => {
  return hasPermission(userPermissions, 'Approve Document');
};

export const canRejectDocument = (userPermissions) => {
  return hasPermission(userPermissions, 'Reject Document');
};

export const canRequestDocumentRevision = (userPermissions) => {
  return hasPermission(userPermissions, 'Request Document Revision');
};

export const canAddVerificationComment = (userPermissions) => {
  return hasPermission(userPermissions, 'Add Verification Comment');
};

export const canUploadVerificationDocument = (userPermissions) => {
  return hasPermission(userPermissions, 'Upload Verification Document');
};

export const canDownloadVerificationDocument = (userPermissions) => {
  return hasPermission(userPermissions, 'Download Verification Document');
};

// Master Data permission helpers
export const canExportMasterData = (userPermissions) => {
  return hasPermission(userPermissions, 'Export Master Data');
};

export const canImportMasterData = (userPermissions) => {
  return hasPermission(userPermissions, 'Import Master Data');
};

export const canBulkUpdateMasterData = (userPermissions) => {
  return hasPermission(userPermissions, 'Bulk Update Master Data');
};

export const canValidateMasterData = (userPermissions) => {
  return hasPermission(userPermissions, 'Validate Master Data');
};

// Product-specific permission helpers
export const canUpdateProductLogo = (userPermissions) => {
  return hasPermission(userPermissions, 'Update Product Logo');
};

export const canUploadProductTemplate = (userPermissions) => {
  return hasPermission(userPermissions, 'Upload Product Template');
};

export const canDeleteProductTemplate = (userPermissions) => {
  return hasPermission(userPermissions, 'Delete Product Template');
};

export const canUpdateProductStatus = (userPermissions) => {
  return hasPermission(userPermissions, 'Update Product Status');
};

// Email Gateway permission helpers
export const canTestEmailGateway = (userPermissions) => {
  return hasPermission(userPermissions, 'Test Email Gateway');
};

/**
 * Get singular form of section name for action permissions
 * @param {string} section - Section name
 * @returns {string} Singular form
 */
const getSingularForm = (section) => {
  const singularMap = {
    'Users': 'User',
    'Groups': 'Group',
    'Forms': 'Form',
    'Form Submissions': 'Form Submission',
    'Master Data': 'Master Data',
    'Tasks': 'Task',
    'Client Types': 'Client Type',
    'Products': 'Product',
    'Upload Fields': 'Upload Field',
    'Verification Types': 'Verification Type',
    'Email Gateways': 'Email Gateway',
    'Documents': 'Document',
    'Reports': 'Report',
    'System Settings': 'System Setting'
  };
  
  return singularMap[section] || section;
};

/**
 * Get all permissions for a specific section
 * @param {Array} userPermissions - Array of user's permissions
 * @param {string} section - Section name (e.g., 'Users', 'Forms', 'Tasks')
 * @returns {Array} Array of permissions for the section
 */
export const getSectionPermissions = (userPermissions, section) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return [];
  
  const singularSection = getSingularForm(section);
  
  return userPermissions.filter(permission => 
    permission.includes(section) || 
    permission.includes(singularSection) ||
    (section === 'Admin Settings' && (
      permission.includes('Client Type') || 
      permission.includes('Product') || 
      permission.includes('Upload Field') || 
      permission.includes('Verification Type') || 
      permission.includes('Email Gateway')
    ))
  );
};

/**
 * Check if user has full access to a section (view, create, update, delete)
 * @param {Array} userPermissions - Array of user's permissions
 * @param {string} section - Section name (e.g., 'Users', 'Forms', 'Tasks')
 * @returns {boolean}
 */
export const hasFullAccess = (userPermissions, section) => {
  const singularSection = getSingularForm(section);
  const requiredPermissions = [
    `View ${section}`,
    `Create ${singularSection}`,
    `Update ${singularSection}`,
    `Delete ${singularSection}`
  ];
  return hasAllPermissions(userPermissions, requiredPermissions);
};

/**
 * Check if user has read-only access to a section (only view)
 * @param {Array} userPermissions - Array of user's permissions
 * @param {string} section - Section name (e.g., 'Users', 'Forms', 'Tasks')
 * @returns {boolean}
 */
export const hasReadOnlyAccess = (userPermissions, section) => {
  return canView(userPermissions, section) && 
         !canCreate(userPermissions, section) && 
         !canUpdate(userPermissions, section) && 
         !canDelete(userPermissions, section);
};

/**
 * Get permission level for a section
 * @param {Array} userPermissions - Array of user's permissions
 * @param {string} section - Section name (e.g., 'Users', 'Forms', 'Tasks')
 * @returns {string} 'full', 'read-only', 'partial', or 'none'
 */
export const getPermissionLevel = (userPermissions, section) => {
  if (!canView(userPermissions, section)) return 'none';
  if (hasFullAccess(userPermissions, section)) return 'full';
  if (hasReadOnlyAccess(userPermissions, section)) return 'read-only';
  return 'partial';
}; 